/**
 * DÍA 2 — Listening + Pronunciation.
 *
 * Flujo: percepción → ejemplo auténtico → imitación breve → grabación y
 * comparación → producción propia → conversación.
 *
 * Reglas duras (CURRICULUM.md §4 y §11):
 *  - Micro-shadowing 5–8 minutos. NUNCA el centro de la sesión.
 *  - Accent Coach: un contraste por sesión, 5–12 minutos, y SIEMPRE con
 *    fallback explícito si todavía no hay target — que es el caso normal en la
 *    Semana 1, porque el baseline acaba de pasar.
 *  - YouGlish nunca sin un target concreto o ejemplos de búsqueda.
 */

import type { Sesion } from '../../tipos.js';

/** Fallback compartido: qué hacer cuando todavía no hay target fonético propio. */
const SIN_TARGET_FONETICO =
  'Todavía no tenés un objetivo fonético propio guardado en el Learning Bank. No abras Accent Coach a navegar al azar: hoy trabajá connected speech con YouGlish. Buscá una frase corriente, escuchá cómo se pega y se reduce en habla real, y quedate con UN patrón.';

export const D2: Sesion = {
  clave: 'd2',
  tipo: 'dia',
  etiqueta: 'Día 2 · Listening + Pronunciation',
  titulo: 'Día 2 · Oír mejor para producir mejor',
  corto: 'Oír/Pron.',
  objetivo:
    'Mejorar percepción, connected speech y pronunciación sin convertir el shadowing en el centro del programa.',
  porQue:
    'La evidencia favorece el entrenamiento perceptivo dirigido. La imitación funciona mejor cuando termina en producción propia, no cuando se acumula por minutos.',
  areasAEvaluar: ['listening', 'pronunciacion_prosodia'],
  capturaCorrecciones: false,
  planes: {
    20: {
      duracion: 20,
      pasos: [
        {
          clave: 'd2-percepcion',
          titulo: 'Percepción dirigida',
          minutosSugeridos: 8,
          herramienta: 'accentCoach',
          objetivo: 'Distinguir un contraste o patrón concreto.',
          porQue:
            'Es difícil producir de forma consistente una diferencia que tu oído todavía no distingue con claridad. Primero el oído, después la boca.',
          instrucciones: [
            'Trabajá UN solo contraste o patrón. Uno.',
            'No abras veinte ejercicios distintos: eso no entrena nada.',
            'Buscá varios ejemplos y voces del mismo contraste.',
          ],
          uso: {
            queHacer:
              'Abrí Accent Coach y trabajá únicamente el contraste que tenés guardado como target. Un contraste, varias voces.',
            requiereTarget: true,
            fallbackSinTarget: SIN_TARGET_FONETICO,
          },
          areas: ['pronunciacion_prosodia', 'listening'],
        },
        {
          clave: 'd2-shadowing',
          titulo: 'Micro-shadowing',
          minutosSugeridos: 5,
          herramienta: 'grabadora',
          grabadora: true,
          objetivo: 'Imitar con precisión un fragmento corto.',
          porQue:
            'El objetivo no es acumular minutos de imitación, sino notar y reproducir ritmo, stress y linking. Cinco minutos atentos valen más que treinta en piloto automático.',
          instrucciones: [
            'Usá 20 a 40 segundos de audio. No más.',
            'Imitalo varias veces prestando atención al ritmo, no a los sonidos sueltos.',
            'Grabate y escuchate UNA vez.',
            'No repitas diez veces buscando defectos.',
          ],
          uso: {
            queHacer:
              'Grabate imitando un fragmento de 20–40 segundos y escuchate una vez para comparar el ritmo y el stress.',
          },
          areas: ['pronunciacion_prosodia'],
        },
        {
          clave: 'd2-transferencia',
          titulo: 'Transferencia a habla propia',
          minutosSugeridos: 7,
          herramienta: 'chatgpt',
          prompt: 'd2',
          objetivo: 'Llevar el patrón a frases nuevas y a conversación.',
          porQue:
            'Imitar sin transferir se evapora en cuanto tenés que pensar qué decir. La prueba es si el patrón sobrevive mientras improvisás.',
          instrucciones: [
            'Copiá el prompt y entrá a Voice.',
            'Producí frases NUEVAS con el patrón, no las que imitaste.',
            'Terminá con una conversación corta.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice, producí frases nuevas con el patrón y cerrá con conversación espontánea.',
          },
          areas: ['pronunciacion_prosodia', 'habla_espontanea'],
        },
      ],
    },

    35: {
      duracion: 35,
      pasos: [
        {
          clave: 'd2-percepcion',
          titulo: 'Percepción dirigida',
          minutosSugeridos: 10,
          herramienta: 'accentCoach',
          objetivo: 'Entrenar una distinción relevante para vos.',
          porQue:
            'La variabilidad de ejemplos ayuda a que la percepción generalice, en vez de quedarte reconociendo un solo audio.',
          instrucciones: [
            'Un solo objetivo por sesión.',
            'Buscá varias voces y ejemplos del mismo contraste.',
            'Si empezás a acertar todo sin esfuerzo, el contraste ya no es tu problema: marcalo como dominado.',
          ],
          uso: {
            queHacer:
              'Abrí Accent Coach y trabajá únicamente tu target de pronunciación guardado, con varias voces del mismo contraste.',
            requiereTarget: true,
            fallbackSinTarget: SIN_TARGET_FONETICO,
          },
          areas: ['pronunciacion_prosodia', 'listening'],
        },
        {
          clave: 'd2-shadowing',
          titulo: 'Micro-shadowing con habla real',
          minutosSugeridos: 7,
          herramienta: 'youglish',
          objetivo: 'Capturar connected speech auténtico.',
          porQue:
            'YouGlish te deja oír cómo distintos estadounidenses realizan la misma expresión. Eso muestra el patrón, no una única pronunciación de laboratorio.',
          instrucciones: [
            'Buscá tu target, o una de las frases sugeridas si todavía no tenés uno.',
            'Elegí un fragmento de 20 a 40 segundos.',
            'Imitá el stress, las reductions y el linking.',
            'Cerrá YouGlish antes de pasar al siguiente paso.',
          ],
          uso: {
            queHacer:
              'Buscá una frase concreta en YouGlish, escuchá cómo la dicen varias personas e imitá el ritmo de un fragmento de 20–40 segundos.',
            requiereTarget: false,
            ejemplosBusqueda: [
              'I have to',
              'want to',
              'a lot of',
              'kind of',
              'going to be',
            ],
          },
          areas: ['pronunciacion_prosodia', 'listening'],
        },
        {
          clave: 'd2-produccion',
          titulo: 'Producción propia',
          minutosSugeridos: 13,
          herramienta: 'chatgpt',
          prompt: 'd2',
          objetivo: 'Usar lo practicado sin copiar el audio.',
          porQue: 'La meta final es conversación, no imitación. Imitar es el medio.',
          instrucciones: [
            'Copiá el prompt y entrá a Voice.',
            'Creá frases diferentes de las que imitaste.',
            'Llevalo a conversación espontánea.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice, producí frases nuevas con el patrón y terminá conversando.',
          },
          areas: ['pronunciacion_prosodia', 'habla_espontanea'],
        },
        {
          clave: 'd2-registro',
          titulo: 'Registro del target',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo: 'Si terminás antes, avanzá. No hace falta agotar los 5 minutos.',
          objetivo: 'Conservar solo el objetivo fonético principal.',
          porQue:
            'Trabajar demasiados rasgos a la vez dispersa la atención. Seguimos un target hasta que mejore, en vez de perseguir muchos.',
          instrucciones: [
            'Guardá UN target de pronunciación o percepción en el Learning Bank.',
            'Si ya tenías uno y hoy mejoró, actualizalo en vez de crear otro.',
          ],
        },
      ],
    },

    50: {
      duracion: 50,
      pasos: [
        {
          clave: 'd2-percepcion',
          titulo: 'Percepción dirigida',
          minutosSugeridos: 12,
          herramienta: 'accentCoach',
          objetivo: 'Afilar una distinción concreta.',
          porQue:
            'El entrenamiento perceptivo es uno de los componentes fonéticos con mejor respaldo. Vale la pena darle tiempo real, pero a un solo objetivo.',
          instrucciones: [
            'Un contraste o patrón, no varios.',
            'Buscá múltiples ejemplos con feedback inmediato.',
            'Si acertás casi todo sin esfuerzo, cambiá de target: ese ya no te limita.',
          ],
          uso: {
            queHacer:
              'Abrí Accent Coach y trabajá tu target guardado con múltiples ejemplos y feedback inmediato.',
            requiereTarget: true,
            fallbackSinTarget: SIN_TARGET_FONETICO,
          },
          areas: ['pronunciacion_prosodia', 'listening'],
        },
        {
          clave: 'd2-shadowing',
          titulo: 'Micro-shadowing',
          minutosSugeridos: 8,
          herramienta: 'youglish',
          objetivo: 'Practicar ritmo, reductions y linking sobre habla real.',
          porQue:
            'Los fragmentos cortos permiten atención deliberada. Treinta minutos de shadowing corrido no la permiten.',
          instrucciones: [
            'Buscá tu target o una de las frases sugeridas.',
            'Tomá 20 a 40 segundos.',
            'Escuchá, imitá y compará.',
            'No hagas 30 minutos de shadowing: son 8.',
          ],
          uso: {
            queHacer:
              'Buscá una frase concreta en YouGlish y trabajá un fragmento de 20–40 segundos: escuchar, imitar, comparar.',
            requiereTarget: false,
            ejemplosBusqueda: [
              'I have to',
              'want to',
              'a lot of',
              'kind of',
              'going to be',
              'should have been',
            ],
          },
          areas: ['pronunciacion_prosodia'],
        },
        {
          clave: 'd2-listening',
          titulo: 'Listening auténtico',
          minutosSugeridos: 10,
          herramienta: 'youglish',
          objetivo: 'Entender habla natural sin transcripción en el primer intento.',
          porQue:
            'Queremos que el listening mejore fuera del ejercicio fonético, no solo dentro de él.',
          instrucciones: [
            'Buscá una frase distinta de la del paso anterior.',
            'Escuchá 5 o 6 clips SIN mirar el texto.',
            'Resumí en inglés lo que entendiste.',
            'Comprobá después con la transcripción.',
          ],
          uso: {
            queHacer:
              'Buscá una frase nueva en YouGlish, escuchá 5 o 6 clips sin leer, resumí lo entendido y recién después comprobá.',
            requiereTarget: false,
            ejemplosBusqueda: [
              'the thing is',
              'it turns out',
              'as far as I know',
              'I ended up',
            ],
          },
          areas: ['listening'],
        },
        {
          clave: 'd2-transferencia',
          titulo: 'Transferencia por Voice',
          minutosSugeridos: 15,
          herramienta: 'chatgpt',
          prompt: 'd2',
          objetivo: 'Mantener el patrón durante habla espontánea.',
          porQue:
            'Acá se comprueba si la pronunciación sobrevive cuando además tenés que pensar qué decir. Ese es el escenario real.',
          instrucciones: [
            'Copiá el prompt y entrá a Voice.',
            'Usá frases nuevas, no las imitadas.',
            'Terminá con una mini conversación sin pensar en el patrón.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice, usá el patrón en frases nuevas y cerrá con conversación espontánea.',
          },
          areas: ['pronunciacion_prosodia', 'habla_espontanea'],
        },
        {
          clave: 'd2-registro',
          titulo: 'Registro del target',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo: 'Si terminás antes, avanzá. No hace falta agotar los 5 minutos.',
          objetivo: 'Guardar una sola prioridad fonética.',
          porQue: 'Seguimos un target hasta que mejore, en vez de perseguir muchos a la vez.',
          instrucciones: [
            'Guardá solo el target principal.',
            'Si el de la semana pasada ya mejoró, marcalo como dominado.',
          ],
        },
      ],
    },
  },
};
